import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Compass } from "lucide-react";
import { api } from "../lib/api";

const mkId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: mkId(), role: "assistant", text: "Hello — I'm the in-house concierge. Ask me anything about Abu Dhabi, Dubai, China, London at Christmas, or where to wander next." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, loading]);

  // Allow any other component to open the chat via window.dispatchEvent(new Event('open-chatbot'))
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-chatbot", handler);
    return () => window.removeEventListener("open-chatbot", handler);
  }, []);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setMessages(m => [...m, { id: mkId(), role: "user", text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { message: text, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages(m => [...m, { id: mkId(), role: "assistant", text: data.reply }]);
    } catch (err) {
      setMessages(m => [...m, { id: mkId(), role: "assistant", text: "Sorry — the concierge is briefly unavailable. Please try again." }]);
    } finally { setLoading(false); }
  };

  return (
    <>
      {/* Floating chatbot button — hidden by user request; chat still opens via the home-page
          'Ask the concierge' button (window.dispatchEvent(new Event('open-chatbot'))). */}

      {open && (
        <div className="fixed inset-0 z-40 md:inset-auto md:bottom-20 md:right-6 md:w-[420px] md:h-[600px] bg-surface border border-line shadow-2xl flex flex-col" data-testid="chatbot-panel">
          <div className="flex items-center justify-between px-5 py-4 bg-olive text-bg">
            <div>
              <div className="overline text-bg/70">Ask The Where To Go</div>
              <div className="font-serif text-xl">Trip concierge</div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-bg/10" data-testid="chatbot-close-btn" aria-label="Close">
              <X size={18}/>
            </button>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4 bg-bg">
            {messages.map((m, i) => (
              <div key={m.id} className={`max-w-[85%] ${m.role === "user" ? "ml-auto" : ""}`} data-testid={`chat-msg-${m.role}-${i}`}>
                <div className={`px-4 py-3 text-[0.95rem] leading-relaxed ${m.role === "user" ? "bg-alt text-ink" : "bg-olive text-bg"}`}>{m.text}</div>
              </div>
            ))}
            {loading && (
              <div className="max-w-[85%]"><div className="px-4 py-3 bg-olive text-bg text-sm">
                <span className="inline-block w-2 h-2 bg-bg rounded-full animate-pulse mr-1"/>
                <span className="inline-block w-2 h-2 bg-bg rounded-full animate-pulse mr-1" style={{animationDelay:'0.15s'}}/>
                <span className="inline-block w-2 h-2 bg-bg rounded-full animate-pulse" style={{animationDelay:'0.3s'}}/>
              </div></div>
            )}
          </div>
          <form onSubmit={send} className="flex border-t border-line">
            <input value={input} onChange={e => setInput(e.target.value)} placeholder="Ask about a destination…" className="flex-1 px-5 py-4 bg-transparent outline-none text-[0.95rem]" data-testid="chatbot-input"/>
            <button type="submit" disabled={loading} className="px-5 bg-ink text-bg hover:bg-olive transition-colors disabled:opacity-50" data-testid="chatbot-send-btn" aria-label="Send"><Send size={18}/></button>
          </form>
        </div>
      )}
    </>
  );
}
